// DialogInjetarCookies — Modal para colar e injetar cookies no navegador remoto.
//
// Aceita 2 modos:
//   1) "cookies.txt" — formato Netscape exportado pela extensão "Get cookies.txt LOCALLY"
//      (Chrome Web Store: cclelndahbckbenkjhflpdbgdldlbecc)
//   2) "Cookie header" — string copiada do DevTools Network → Request Headers → Cookie
//      (precisa informar o domínio alvo)
//
// Após injetar, opcionalmente navega para o domínio para forçar o site a usar os cookies.

import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Cookie, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { injetarCookies } from "@/lib/api";

export default function DialogInjetarCookies({ aberto, setAberto, botId, botStatus, onSucesso }) {
    const [modo, setModo] = useState("netscape"); // "netscape" | "header"
    const [textoNetscape, setTextoNetscape] = useState("");
    const [cookieHeader, setCookieHeader] = useState("");
    const [dominioHeader, setDominioHeader] = useState("");
    const [navegarApos, setNavegarApos] = useState(true);
    const [dominioParaNavegar, setDominioParaNavegar] = useState("");
    const [enviando, setEnviando] = useState(false);

    const rodando = botStatus === "rodando";

    const limpar = () => {
        setTextoNetscape("");
        setCookieHeader("");
        setDominioHeader("");
        setDominioParaNavegar("");
    };

    const enviar = async () => {
        if (!rodando) {
            toast.error("Inicie o bot antes de injetar cookies");
            return;
        }
        let payload;
        if (modo === "netscape") {
            const txt = textoNetscape.trim();
            if (!txt) {
                toast.error("Cole o conteúdo do arquivo cookies.txt");
                return;
            }
            payload = {
                cookies_txt: txt,
                navegar: navegarApos,
                dominio: dominioParaNavegar.trim(),
            };
        } else {
            const hdr = cookieHeader.trim();
            const dom = dominioHeader.trim();
            if (!hdr || !dom) {
                toast.error("Informe o header Cookie e o domínio");
                return;
            }
            payload = {
                cookie_header: hdr,
                dominio: dom,
                navegar: navegarApos,
            };
        }

        setEnviando(true);
        try {
            const res = await injetarCookies(botId, payload);
            const total = res?.total_recebidos ?? 0;
            const ok = res?.injetados ?? 0;
            const erros = res?.erros || [];
            if (ok > 0 && erros.length === 0) {
                toast.success(`${ok} cookie${ok > 1 ? "s" : ""} injetado${ok > 1 ? "s" : ""} com sucesso${navegarApos ? " — navegando…" : ""}`);
            } else if (ok > 0 && erros.length > 0) {
                toast.warning(`${ok}/${total} cookies injetados — ${erros.length} falharam`);
            } else {
                toast.error("Nenhum cookie foi injetado");
            }
            if (onSucesso) onSucesso();
            limpar();
            setAberto(false);
        } catch (e) {
            const msg = e?.response?.data?.erro || e?.message || "Falha ao injetar cookies";
            toast.error(msg);
        } finally {
            setEnviando(false);
        }
    };

    return (
        <Dialog open={aberto} onOpenChange={setAberto}>
            <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl" data-testid="dialog-injetar-cookies">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Cookie size={18} weight="duotone" />
                        Injetar Cookies no navegador
                    </DialogTitle>
                    <DialogDescription className="text-zinc-400 text-xs">
                        Colar o <code className="bg-zinc-900 px-1 rounded">cookies.txt</code> baixado pela extensão
                        {" "}
                        <a
                            href="https://chromewebstore.google.com/detail/get-cookiestxt-locally/cclelndahbckbenkjhflpdbgdldlbecc"
                            target="_blank"
                            rel="noreferrer"
                            className="text-emerald-400 hover:underline"
                        >Get cookies.txt LOCALLY</a>
                        {" "}
                        ou o valor do header <code className="bg-zinc-900 px-1 rounded">Cookie</code> direto do DevTools.
                    </DialogDescription>
                </DialogHeader>

                {!rodando && (
                    <div className="flex items-start gap-2 p-2.5 bg-amber-500/10 border border-amber-500/30 rounded-md text-xs text-amber-300">
                        <Warning size={16} weight="fill" className="shrink-0 mt-0.5" />
                        <div>
                            O bot precisa estar <strong>rodando</strong> para injetar cookies.
                            Status atual: <code className="bg-zinc-900 px-1 rounded">{botStatus}</code>.
                        </div>
                    </div>
                )}

                <Tabs value={modo} onValueChange={setModo}>
                    <TabsList className="bg-zinc-900/60 border border-zinc-800 p-1 h-auto">
                        <TabsTrigger value="netscape" className="text-xs px-3 py-1.5 data-[state=active]:bg-zinc-800" data-testid="tab-modo-netscape">
                            cookies.txt (Netscape)
                        </TabsTrigger>
                        <TabsTrigger value="header" className="text-xs px-3 py-1.5 data-[state=active]:bg-zinc-800" data-testid="tab-modo-header">
                            Cookie header
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="netscape" className="mt-3 space-y-3">
                        <div>
                            <Label className="text-xs text-zinc-400 mb-1.5 block">Conteúdo do cookies.txt</Label>
                            <Textarea
                                data-testid="input-cookies-txt"
                                value={textoNetscape}
                                onChange={(e) => setTextoNetscape(e.target.value)}
                                placeholder={`# Netscape HTTP Cookie File\n.google.com\tTRUE\t/\tTRUE\t1900000000\tSID\tvalor_do_cookie\n#HttpOnly_.google.com\tTRUE\t/\tTRUE\t1900000000\tHSID\tvalor_secreto`}
                                rows={9}
                                className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono text-[11px] placeholder:text-zinc-700"
                            />
                        </div>
                        <div>
                            <Label className="text-xs text-zinc-400 mb-1.5 block">
                                Domínio para navegar após injetar <span className="text-zinc-600">(opcional)</span>
                            </Label>
                            <Input
                                data-testid="input-dominio-navegar"
                                value={dominioParaNavegar}
                                onChange={(e) => setDominioParaNavegar(e.target.value)}
                                placeholder="ex.: accounts.google.com ou https://gmail.com"
                                className="bg-zinc-900 border-zinc-800 text-zinc-100 text-xs"
                            />
                        </div>
                    </TabsContent>

                    <TabsContent value="header" className="mt-3 space-y-3">
                        <div>
                            <Label className="text-xs text-zinc-400 mb-1.5 block">Header Cookie (do DevTools)</Label>
                            <Textarea
                                data-testid="input-cookie-header"
                                value={cookieHeader}
                                onChange={(e) => setCookieHeader(e.target.value)}
                                placeholder="ex.: SID=abc123; HSID=xyz; SSID=qwe; APISID=..."
                                rows={5}
                                className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono text-[11px] placeholder:text-zinc-700"
                            />
                        </div>
                        <div>
                            <Label className="text-xs text-zinc-400 mb-1.5 block">Domínio dos cookies <span className="text-red-400">*</span></Label>
                            <Input
                                data-testid="input-dominio-header"
                                value={dominioHeader}
                                onChange={(e) => {
                                    setDominioHeader(e.target.value);
                                    if (!dominioParaNavegar) setDominioParaNavegar(e.target.value);
                                }}
                                placeholder="ex.: google.com (sem https://)"
                                className="bg-zinc-900 border-zinc-800 text-zinc-100 text-xs"
                            />
                        </div>
                    </TabsContent>
                </Tabs>

                <div className="flex items-center gap-2 pt-1">
                    <Checkbox
                        id="navegar-apos"
                        checked={navegarApos}
                        onCheckedChange={setNavegarApos}
                        className="border-zinc-700"
                        data-testid="check-navegar-apos"
                    />
                    <Label htmlFor="navegar-apos" className="text-xs text-zinc-300 cursor-pointer">
                        Navegar para o domínio após injetar (recomendado)
                    </Label>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => setAberto(false)}
                        disabled={enviando}
                        className="bg-transparent border-zinc-800 hover:bg-zinc-800 text-zinc-300"
                        data-testid="btn-cancelar-injetar"
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={enviar}
                        disabled={enviando || !rodando}
                        className="bg-emerald-500 text-zinc-950 hover:bg-emerald-400 disabled:opacity-50"
                        data-testid="btn-confirmar-injetar"
                    >
                        <CheckCircle size={14} weight="fill" className="mr-1.5" />
                        {enviando ? "Injetando..." : "Injetar cookies"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
